import React, { useState, useEffect } from 'react';
import { formatDate as formatSharedDate } from '@/utils/displayDateTime';
import { Activity, ActivityType } from '../../../types/Activity';
import { areasService } from '../../../services/areasService';
import { ServiceArea } from '../../../types/ForemanArea';
import { usePermissions } from '../../../hooks/usePermissions';
import { Form, Table, Alert, Button, Row, Col, Modal } from '@/components/ui';
import type { ActivityWritePayload } from '../../../services/activityService';
import {
  ActivityFormState,
  BLOCK_TYPE_SUGGESTIONS,
  PROFILE_BOOLEAN_FIELDS,
  PROFILE_FIELD_LABELS,
  emptyActivityForm,
  hasFetchedInstructions,
  toActivityFormState,
  toActivityPayload,
} from './activityFormFields';
import { describeActionError } from '../../../utils/loadError';

// Fields validated on submit, in on-screen order (FORM-01: focus the first error).
type ActivityField = 'activityName' | 'activityNumber' | 'unitOfMeasure' | 'classification' | 'costings';
const FIELD_ORDER: ActivityField[] = ['activityName', 'activityNumber', 'unitOfMeasure', 'classification', 'costings'];
const FIELD_TAB: Record<ActivityField, 'details' | 'costing'> = {
  activityName: 'details',
  activityNumber: 'details',
  unitOfMeasure: 'details',
  classification: 'details',
  costings: 'costing',
};
const FIELD_ID: Record<ActivityField, string> = {
  activityName: 'activityName',
  activityNumber: 'activityNumber',
  unitOfMeasure: 'activityUnitOfMeasure',
  classification: 'activityClassification',
  costings: 'activityNewCostingServiceArea',
};
type FieldErrors = Partial<Record<ActivityField, string>>;

interface ActivityModalProps {
  visible: boolean;
  onClose: () => void;
  onSave: (activity: ActivityWritePayload) => Promise<void>;
  activity?: Activity;
}

const ActivityModal: React.FC<ActivityModalProps> = ({
  visible,
  onClose,
  onSave,
  activity
}) => {
  const [formData, setFormData] = useState<ActivityFormState>(emptyActivityForm());
  const [serviceAreas, setServiceAreas] = useState<ServiceArea[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'details' | 'costing' | 'profile' | 'instructions'>(
    'details'
  );
  const [financeActivityNumber, setFinanceActivityNumber] = useState<string>('');
  const [activityCostings, setActivityCostings] = useState<Array<{
    id?: number;
    serviceAreaId: number;
    contractYear: number;
    scheduled2Rate: number | null;
    serviceArea?: ServiceArea | null;
  }>>([]);
  const [newCosting, setNewCosting] = useState<{
    serviceAreaId: number | null;
    contractYear: number;
    scheduled2Rate: number | null;
  }>({
    serviceAreaId: null,
    contractYear: 6,
    scheduled2Rate: null,
  });
  const [errors, setErrors] = useState<FieldErrors>({});
  const [newCostingError, setNewCostingError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const { hasPermission } = usePermissions();
  const isReadOnly = !hasPermission('activity:edit');

  useEffect(() => {
    if (visible) {
      fetchServiceAreas();
      setErrors({});
      setNewCostingError(null);
      setSaveError(null);
    }
  }, [visible]);

  const clearError = (field: string) => {
    setErrors(prev => {
      if (!prev[field as ActivityField]) return prev;
      const next = { ...prev };
      delete next[field as ActivityField];
      return next;
    });
  };

  // Errors per tab, shown as a count on the tab (FORM-01)
  const tabErrorCount = (tab: 'details' | 'costing') =>
    FIELD_ORDER.filter(f => errors[f] && FIELD_TAB[f] === tab).length;

  useEffect(() => {
    if (activity) {
      // An explicit pick, never a spread: the spread used to carry every column the API
      // returned straight back into the save body, which is how the profile columns broke
      // every edit the day they were added.
      setFormData(toActivityFormState(activity));
      // Load activity costings
      if (activity.activityCostings && activity.activityCostings.length > 0) {
        setActivityCostings(activity.activityCostings.map(costing => ({
          id: costing.id,
          serviceAreaId: costing.serviceAreaId,
          contractYear: costing.contractYear ?? 6,
          scheduled2Rate: costing.scheduled2Rate,
          serviceArea: costing.serviceArea,
        })));
      } else {
        setActivityCostings([]);
      }
      // TODO: Load financeActivityNumber if it exists in the activity
      // This may need to be added to the Activity type/interface
    } else {
      setFormData(emptyActivityForm());
      setFinanceActivityNumber('');
      setActivityCostings([]);
    }
  }, [activity]);

  const fetchServiceAreas = async () => {
    try {
      const areas = await areasService.getServiceAreas();
      setServiceAreas(areas as ServiceArea[]);
    } catch (error) {
      console.error('Error fetching service areas:', error);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    if (!isReadOnly) {
      const { name, value, type } = e.target;
      const checked = (e.target as HTMLInputElement).checked;
      
      if (name === 'financeActivityNumber') {
        setFinanceActivityNumber(value);
        return;
      }
      clearError(name);
      
      setFormData(prev => ({
        ...prev,
        [name]:
          name === 'threshold'
            ? (value === '' ? null : Number(value))
            : type === 'checkbox'
            ? checked
            : value
      }));
    }
  };

  const handleAddCosting = () => {
    if (!newCosting.serviceAreaId || !newCosting.contractYear) {
      setNewCostingError(!newCosting.serviceAreaId ? 'Service area required' : 'Contract year required');
      document.getElementById(FIELD_ID.costings)?.focus();
      return;
    }
    
    // Check if this combination already exists
    const exists = activityCostings.some(
      c => c.serviceAreaId === newCosting.serviceAreaId && 
           c.contractYear === newCosting.contractYear
    );
    
    if (exists) {
      setNewCostingError('A costing for this service area and contract year already exists');
      document.getElementById(FIELD_ID.costings)?.focus();
      return;
    }
    setNewCostingError(null);
    clearError('costings');
    
    const serviceArea = serviceAreas.find(a => a.id === newCosting.serviceAreaId);
    setActivityCostings([...activityCostings, {
      ...newCosting,
      serviceAreaId: newCosting.serviceAreaId!,
      serviceArea: serviceArea || null,
    }]);
    
    // Reset new costing form
    setNewCosting({
      serviceAreaId: null,
      contractYear: 6,
      scheduled2Rate: null,
    });
  };

  const handleRemoveCosting = (index: number) => {
    setActivityCostings(activityCostings.filter((_, i) => i !== index));
  };

  const handleUpdateCosting = (index: number, field: string, value: any) => {
    const updated = [...activityCostings];
    updated[index] = { ...updated[index], [field]: value };
    setActivityCostings(updated);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaveError(null);

    // Validate required fields – each problem shows under its field (FORM-01)
    const fieldErrors: FieldErrors = {};
    if (!formData.activityName) fieldErrors.activityName = 'Activity name required';
    if (!formData.activityNumber) fieldErrors.activityNumber = 'Activity number required';
    if (!formData.unitOfMeasure) fieldErrors.unitOfMeasure = 'Unit of measure required';
    if (!formData.classification) fieldErrors.classification = 'Classification required';
    // At least one costing must exist
    if (activityCostings.length === 0) fieldErrors.costings = 'Add at least one activity costing';
    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors);
      const first = FIELD_ORDER.find(f => fieldErrors[f])!;
      setActiveTab(FIELD_TAB[first]);
      setTimeout(() => document.getElementById(FIELD_ID[first])?.focus(), 0);
      return;
    }

    try {
      setLoading(true);
      // Include activity costings in the data
      // Remove serviceAreaId, contractYear, scheduled2Rate from formData as they're now in costings
      const dataToSave = {
        ...toActivityPayload(formData, {
          includeWorkInstructions: hasFetchedInstructions(activity),
        }),
        activityCostings: activityCostings.map(c => {
          const costing: any = {
            serviceAreaId: c.serviceAreaId,
            contractYear: c.contractYear,
            scheduled2Rate: c.scheduled2Rate,
          };
          // Only include id if it's an existing costing (id > 0)
          if (c.id && c.id > 0) {
            costing.id = c.id;
          }
          return costing;
        }),
        // financeActivityNumber would need to be added to the Activity type/backend
      };
      await onSave(dataToSave);
      onClose();
    } catch (error) {
      console.error('Error saving activity:', error);
      setSaveError(describeActionError(error, "Couldn't save the activity. Try again."));
    } finally {
      setLoading(false);
    }
  };

  /**
   * The profile name this activity actually carries.
   *
   * Activity has two free-text profile columns and which one is populated varies by database:
   * the training database holds every name in `profile` with `activityProfile` blank. Reading
   * only the first shows "None" beside a fully populated set of flags, which reads as a bug and
   * invites someone to retype a name that is already there.
   */
  const effectiveProfileName = activity?.activityProfile || activity?.profile || '';

  /** False when the prose was not fetched, which is not the same as there being none. */
  const instructionsFetched = hasFetchedInstructions(activity);

  const formatDate = (value: string) => formatSharedDate(value);

  /**
   * Whether the flags on screen came from the export or from somebody here.
   *
   * The warning is the one that matters: the import overwrites hand edits, and without saying
   * so the revert happens weeks later, to somebody who never saw this screen.
   */
  const profileBanner = (() => {
    const editedAt = activity?.profileEditedAt;
    const loadedAt = activity?.profileLoadedAt;

    if (editedAt) {
      return (
        <Alert variant="warning">
          Edited by hand on {formatDate(editedAt)}. The Activity Profiles import will replace
          these values the next time it runs. Change the export to make a change permanent.
        </Alert>
      );
    }
    if (loadedAt) {
      return (
        <Alert variant="info">
          Loaded from the Activity Profiles export on {formatDate(loadedAt)}. Edits here are
          allowed, but the import wins.
        </Alert>
      );
    }
    return (
      <Alert variant="secondary">
        No profile from the export matched this activity, so these flags are defaults rather
        than data. Setting them here gives the mobile app something to work from.
      </Alert>
    );
  })();

  return (
    <Modal show={visible} onHide={onClose} size="xl" backdrop={loading ? 'static' : true}>
      <Modal.Header closeButton>
        <Modal.Title>{activity ? 'Edit Activity' : 'Create Activity'}</Modal.Title>
      </Modal.Header>
      {/* Form wraps Body+Footer (footer holds the submit) and is the flex column itself. */}
      <form onSubmit={handleSubmit} noValidate className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col">
            <Modal.Body>
              {saveError && (
                <Alert variant="danger" role="alert" className="tw-mb-3">
                  {saveError}
                </Alert>
              )}
              {/* Tab Navigation */}
              <ul className="tw-mb-3 tw-flex tw-flex-wrap tw-gap-1 tw-border-b tw-border-line tw-list-none tw-p-0" role="tablist">
                <li role="presentation">
                  <button
                    className={`-tw-mb-px tw-border-b-2 tw-px-3 tw-py-2 ${activeTab === 'details' ? 'tw-border-primary tw-font-medium tw-text-primary' : 'tw-border-transparent tw-text-content-secondary hover:tw-text-content'}`}
                    type="button"
                    onClick={() => setActiveTab('details')}
                  >
                    Activity Details <span className="tw-text-danger">*</span>
                    {tabErrorCount('details') > 0 && <span className="tw-ml-1 tw-text-danger">({tabErrorCount('details')})</span>}
                  </button>
                </li>
                <li role="presentation">
                  <button
                    className={`-tw-mb-px tw-border-b-2 tw-px-3 tw-py-2 ${activeTab === 'costing' ? 'tw-border-primary tw-font-medium tw-text-primary' : 'tw-border-transparent tw-text-content-secondary hover:tw-text-content'}`}
                    type="button"
                    onClick={() => setActiveTab('costing')}
                  >
                    Activity Costing <span className="tw-text-danger">*</span>
                    {tabErrorCount('costing') > 0 && <span className="tw-ml-1 tw-text-danger">({tabErrorCount('costing')})</span>}
                  </button>
                </li>
                <li role="presentation">
                  <button
                    className={`-tw-mb-px tw-border-b-2 tw-px-3 tw-py-2 ${activeTab === 'profile' ? 'tw-border-primary tw-font-medium tw-text-primary' : 'tw-border-transparent tw-text-content-secondary hover:tw-text-content'}`}
                    type="button"
                    onClick={() => setActiveTab('profile')}
                  >
                    Profile
                  </button>
                </li>
                <li role="presentation">
                  <button
                    className={`-tw-mb-px tw-border-b-2 tw-px-3 tw-py-2 ${activeTab === 'instructions' ? 'tw-border-primary tw-font-medium tw-text-primary' : 'tw-border-transparent tw-text-content-secondary hover:tw-text-content'}`}
                    type="button"
                    onClick={() => setActiveTab('instructions')}
                  >
                    Work Instructions
                  </button>
                </li>
              </ul>

              {/* Tab Content */}
              <div>
                {/* Activity Details Tab */}
                {activeTab === 'details' && (
                  <div>
                    <div className="tw-mb-4">
                      <Form.Label htmlFor={FIELD_ID.activityName}>Activity Name <span className="tw-text-danger">*</span></Form.Label>
                      <Form.Control
                        id={FIELD_ID.activityName}
                        type="text"
                        name="activityName"
                        value={formData.activityName || ''}
                        onChange={handleChange}
                        readOnly={isReadOnly}
                        required
                        isInvalid={!!errors.activityName}
                      />
                      {errors.activityName && <Form.Control.Feedback type="invalid">{errors.activityName}</Form.Control.Feedback>}
                    </div>
                    <div className="tw-mb-4">
                      <Form.Label htmlFor={FIELD_ID.activityNumber}>Activity Number <span className="tw-text-danger">*</span></Form.Label>
                      <Form.Control
                        id={FIELD_ID.activityNumber}
                        type="text"
                        name="activityNumber"
                        value={formData.activityNumber || ''}
                        onChange={handleChange}
                        readOnly={isReadOnly}
                        required
                        isInvalid={!!errors.activityNumber}
                      />
                      {errors.activityNumber && <Form.Control.Feedback type="invalid">{errors.activityNumber}</Form.Control.Feedback>}
                    </div>
                    <div className="tw-mb-4">
                      <Form.Label htmlFor={FIELD_ID.unitOfMeasure}>Unit of Measure <span className="tw-text-danger">*</span></Form.Label>
                      <Form.Control
                        id={FIELD_ID.unitOfMeasure}
                        type="text"
                        name="unitOfMeasure"
                        value={formData.unitOfMeasure || ''}
                        onChange={handleChange}
                        readOnly={isReadOnly}
                        required
                        isInvalid={!!errors.unitOfMeasure}
                      />
                      {errors.unitOfMeasure && <Form.Control.Feedback type="invalid">{errors.unitOfMeasure}</Form.Control.Feedback>}
                    </div>
                    <div className="tw-mb-4">
                      <Form.Label htmlFor={FIELD_ID.classification}>Classification <span className="tw-text-danger">*</span></Form.Label>
                      <Form.Select
                        id={FIELD_ID.classification}
                        name="classification"
                        value={formData.classification || ''}
                        onChange={handleChange}
                        disabled={isReadOnly}
                        required
                        isInvalid={!!errors.classification}
                      >
                        <option value="">Select Classification</option>
                        {Object.values(ActivityType).map((type) => (
                          <option key={type} value={type.toLowerCase()}>{type}</option>
                        ))}
                      </Form.Select>
                      {errors.classification && <Form.Control.Feedback type="invalid">{errors.classification}</Form.Control.Feedback>}
                    </div>
                    <div className="tw-mb-4">
                      <Form.Label>Activity Type</Form.Label>
                      <Form.Select
                        name="activityType"
                        value={formData.activityType || ''}
                        onChange={handleChange}
                        disabled={isReadOnly}
                      >
                        <option value="">Select Activity Type</option>
                        <option value="Routine">Routine</option>
                        <option value="Quantified">Quantified</option>
                      </Form.Select>
                    </div>
                    <div className="tw-mb-4">
                      <Form.Label>Finance Activity Number</Form.Label>
                      <Form.Control
                        type="text"
                        name="financeActivityNumber"
                        value={financeActivityNumber}
                        onChange={handleChange}
                        readOnly={isReadOnly}
                        placeholder="Enter finance activity number"
                      />
                    </div>
                    <div className="tw-mb-4">
                      <Form.Label>Location Code</Form.Label>
                      <Form.Select
                        name="locationCode"
                        value={formData.locationCode || ''}
                        onChange={handleChange}
                        disabled={isReadOnly}
                      >
                        <option value="">Select Location Code</option>
                        <option value="A">A</option>
                        <option value="B">B</option>
                        <option value="C">C</option>
                      </Form.Select>
                    </div>
                    <div className="tw-mb-4">
                      <Form.Label>Profile</Form.Label>
                      <Form.Control
                        type="text"
                        name="activityProfile"
                        value={formData.activityProfile || ''}
                        onChange={handleChange}
                        readOnly={isReadOnly}
                        placeholder="Enter profile"
                      />
                    </div>
                    <div className="tw-mb-4">
                      <Form.Label>Threshold</Form.Label>
                      <Form.Control
                        type="number"
                        name="threshold"
                        value={formData.threshold ?? ''}
                        onChange={handleChange}
                        readOnly={isReadOnly}
                        step="0.01"
                        placeholder="Optional quantity limit"
                      />
                    </div>
                    <div className="tw-mb-4">
                      <Form.Label>Linear/Point</Form.Label>
                      <Form.Select
                        name="linearPoint"
                        value={
                          formData.linear ? 'linear' : 
                          formData.point ? 'point' : 
                          ''
                        }
                        onChange={(e) => {
                          if (!isReadOnly) {
                            const value = e.target.value;
                            setFormData(prev => ({
                              ...prev,
                              linear: value === 'linear',
                              point: value === 'point'
                            }));
                          }
                        }}
                        disabled={isReadOnly}
                      >
                        <option value="">Select option</option>
                        <option value="linear">Linear</option>
                        <option value="point">Point</option>
                      </Form.Select>
                    </div>
                  </div>
                )}

                {/* Activity Costing Tab */}
                {activeTab === 'costing' && (
                  <div>
                    <h6 className="tw-mb-3">Existing Activity Costings</h6>
                    {activityCostings.length > 0 ? (
                      <div className="tw-overflow-x-auto tw-mb-4">
                        <Table bordered size="sm">
                          <thead>
                            <tr>
                              <th>Service Area</th>
                              <th>Contract Year</th>
                              <th>Scheduled Rate</th>
                              {!isReadOnly && <th>Actions</th>}
                            </tr>
                          </thead>
                          <tbody>
                            {activityCostings.map((costing, index) => (
                              <tr key={index}>
                                <td>
                                  {costing.serviceArea?.name || 
                                   serviceAreas.find(a => a.id === costing.serviceAreaId)?.name || 
                                   `Area ${costing.serviceAreaId}`}
                                </td>
                                <td>{costing.contractYear}</td>
                                <td>
                                  {isReadOnly ? (
                                    costing.scheduled2Rate?.toFixed(2) || '0.00'
                                  ) : (
                                    <Form.Control
                                      type="number"
                                      size="sm"
                                      value={costing.scheduled2Rate ?? ''}
                                      onChange={(e) => handleUpdateCosting(
                                        index,
                                        'scheduled2Rate',
                                        e.target.value === '' ? null : parseFloat(e.target.value)
                                      )}
                                      step="0.01"
                                      placeholder="0.00"
                                    />
                                  )}
                                </td>
                                {!isReadOnly && (
                                  <td>
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="danger"
                                      onClick={() => handleRemoveCosting(index)}
                                    >
                                      Remove
                                    </Button>
                                  </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    ) : (
                      <Alert variant="info" className="tw-mb-4">
                        No activity costings added yet. Add one below.
                      </Alert>
                    )}
                    {errors.costings && (
                      <Form.Control.Feedback type="invalid" className="tw-mb-4">{errors.costings}</Form.Control.Feedback>
                    )}

                    {!isReadOnly && (
                      <>
                        <hr className="tw-my-4 tw-border-t tw-border-line" />
                        <h6 className="tw-mb-3">Add New Activity Costing</h6>
                        <Row>
                          <Col md={3}>
                            <Form.Label>Contract Year <span className="tw-text-danger">*</span></Form.Label>
                            <Form.Select
                              value={newCosting.contractYear}
                              onChange={(e) => setNewCosting({
                                ...newCosting,
                                contractYear: parseInt(e.target.value)
                              })}
                            >
                              <option value="6">6</option>
                              <option value="7">7</option>
                              <option value="8">8</option>
                            </Form.Select>
                          </Col>
                          <Col md={4}>
                            <Form.Label htmlFor={FIELD_ID.costings}>Service Area <span className="tw-text-danger">*</span></Form.Label>
                            <Form.Select
                              id={FIELD_ID.costings}
                              value={newCosting.serviceAreaId ?? ''}
                              onChange={(e) => {
                                setNewCosting({
                                  ...newCosting,
                                  serviceAreaId: e.target.value === '' ? null : parseInt(e.target.value)
                                });
                                setNewCostingError(null);
                              }}
                              isInvalid={!!newCostingError}
                            >
                              <option value="">Select Service Area</option>
                              {serviceAreas
                                .filter(area =>
                                  !activityCostings.some(
                                    c => c.serviceAreaId === area.id &&
                                         c.contractYear === newCosting.contractYear
                                  )
                                )
                                .map(area => (
                                  <option key={area.id} value={area.id.toString()}>
                                    {area.name}
                                  </option>
                                ))}
                            </Form.Select>
                            {newCostingError && <Form.Control.Feedback type="invalid">{newCostingError}</Form.Control.Feedback>}
                          </Col>
                          <Col md={3}>
                            <Form.Label>Scheduled Rate</Form.Label>
                            <Form.Control
                              type="number"
                              value={newCosting.scheduled2Rate ?? ''}
                              onChange={(e) => setNewCosting({
                                ...newCosting,
                                scheduled2Rate: e.target.value === '' ? null : parseFloat(e.target.value)
                              })}
                              step="0.01"
                              placeholder="0.00"
                            />
                          </Col>
                          <Col md={2} className="tw-flex tw-items-end">
                            <Button
                              type="button"
                              variant="primary"
                              onClick={handleAddCosting}
                            >
                              Add Costing
                            </Button>
                          </Col>
                        </Row>
                      </>
                    )}
                  </div>
                )}

                {/* Profile Tab */}
                {activeTab === 'profile' && (
                  <div>
                    {profileBanner}
                    <div className="tw-mb-4">
                      <Form.Label>Profile</Form.Label>
                      <Form.Control type="text" value={effectiveProfileName || 'None'} readOnly />
                      <Form.Text>
                        The name the import matches on. Edit it under Activity Details.
                      </Form.Text>
                    </div>

                    <Row>
                      {PROFILE_BOOLEAN_FIELDS.map(field => (
                        <Col md={6} key={field} className="tw-mb-2">
                          <Form.Check
                            type="checkbox"
                            id={`activity-profile-${field}`}
                            name={field}
                            label={PROFILE_FIELD_LABELS[field]}
                            checked={formData[field]}
                            onChange={handleChange}
                            disabled={isReadOnly}
                          />
                        </Col>
                      ))}
                    </Row>

                    <Row className="tw-mt-3">
                      <Col md={6} className="tw-mb-4">
                        <Form.Label htmlFor="activity-restAreaActivity">Rest area activity</Form.Label>
                        <Form.Select
                          id="activity-restAreaActivity"
                          name="restAreaActivity"
                          value={formData.restAreaActivity}
                          onChange={handleChange}
                          disabled={isReadOnly}
                        >
                          {/* The blank option is named, so nobody reads it as unset. */}
                          <option value="">Do not ask</option>
                          <option value="OPTIONAL">Optional</option>
                          <option value="MANDATORY">Mandatory</option>
                        </Form.Select>
                      </Col>
                      <Col md={6} className="tw-mb-4">
                        <Form.Label htmlFor="activity-structureActivity">Structure activity</Form.Label>
                        <Form.Select
                          id="activity-structureActivity"
                          name="structureActivity"
                          value={formData.structureActivity}
                          onChange={handleChange}
                          disabled={isReadOnly}
                        >
                          <option value="">Do not ask</option>
                          <option value="OPTIONAL">Optional</option>
                          <option value="MANDATORY">Mandatory</option>
                        </Form.Select>
                      </Col>
                      <Col md={6} className="tw-mb-4">
                        <Form.Label htmlFor="activity-displayEntireRoadQuestion">Entire road question</Form.Label>
                        <Form.Select
                          id="activity-displayEntireRoadQuestion"
                          name="displayEntireRoadQuestion"
                          value={formData.displayEntireRoadQuestion}
                          onChange={handleChange}
                          disabled={isReadOnly}
                        >
                          <option value="">Do not show the question</option>
                          <option value="CHECKED">Show, ticked by default</option>
                          <option value="UNCHECKED">Show, unticked by default</option>
                        </Form.Select>
                      </Col>
                      <Col md={6} className="tw-mb-4">
                        <Form.Label htmlFor="activity-numberedRoadActivity">Numbered road activity</Form.Label>
                        <Form.Select
                          id="activity-numberedRoadActivity"
                          name="numberedRoadActivity"
                          value={formData.numberedRoadActivity}
                          onChange={handleChange}
                          disabled={isReadOnly}
                        >
                          {/* Not applicable and No are different instructions, not two ways of
                              saying nothing: No restricts to non-numbered roads. */}
                          <option value="">Not applicable</option>
                          <option value="true">Numbered roads only</option>
                          <option value="false">Non-numbered roads only</option>
                        </Form.Select>
                      </Col>
                      <Col md={6} className="tw-mb-4">
                        <Form.Label htmlFor="activity-numberOfPasses">Number of passes</Form.Label>
                        <Form.Control
                          type="number"
                          min={0}
                          step={1}
                          id="activity-numberOfPasses"
                          name="numberOfPasses"
                          value={formData.numberOfPasses}
                          onChange={handleChange}
                          readOnly={isReadOnly}
                        />
                      </Col>
                      <Col md={6} className="tw-mb-4">
                        <Form.Label htmlFor="activity-blockType">Block type</Form.Label>
                        <Form.Control
                          type="text"
                          id="activity-blockType"
                          name="blockType"
                          list="activity-block-types"
                          value={formData.blockType}
                          onChange={handleChange}
                          readOnly={isReadOnly}
                        />
                        <datalist id="activity-block-types">
                          {BLOCK_TYPE_SUGGESTIONS.map(option => (
                            <option key={option} value={option} />
                          ))}
                        </datalist>
                      </Col>
                    </Row>
                  </div>
                )}

                {/* Work Instructions Tab */}
                {activeTab === 'instructions' && (
                  <div>
                    {!instructionsFetched ? (
                      <Alert variant="warning">
                        This activity has work instructions, but they could not be loaded. Close
                        the modal and reopen it to edit them. Saving now leaves them as they are.
                      </Alert>
                    ) : (
                      <>
                        <Alert variant="info">
                          Work instructions are loaded from the Work Instructions export. Edits
                          here are allowed, but the next import replaces them. Change the export
                          to make a change permanent.
                        </Alert>
                        <div className="tw-mb-4">
                          <Form.Label htmlFor="activity-workInstructionPurpose">Purpose</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={4}
                            id="activity-workInstructionPurpose"
                          name="workInstructionPurpose"
                            value={formData.workInstructionPurpose}
                            onChange={handleChange}
                            readOnly={isReadOnly}
                          />
                        </div>
                        <div className="tw-mb-4">
                          <Form.Label htmlFor="activity-workInstructionSteps">Steps</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={12}
                            id="activity-workInstructionSteps"
                          name="workInstructionSteps"
                            value={formData.workInstructionSteps}
                            onChange={handleChange}
                            readOnly={isReadOnly}
                          />
                          <Form.Text>One step per line. Blank lines are ignored.</Form.Text>
                        </div>
                        {activity?.workInstructions?.updatedAt && (
                          <Form.Text>
                            Last updated {formatDate(activity.workInstructions.updatedAt)}
                          </Form.Text>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button type="button" variant="secondary" onClick={onClose}>
                Cancel
              </Button>
              {!isReadOnly && (
                <Button
                  type="submit"
                  variant="primary"
                  disabled={loading}
                >
                  {loading ? 'Saving...' : (activity ? 'Update' : 'Create')}
                </Button>
              )}
            </Modal.Footer>
          </form>
    </Modal>
  );
};

export default ActivityModal; 